import React, { useCallback, useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { Helmet } from "react-helmet-async";
import {
  getDocs,
  collection,
  limit,
  query,
  orderBy,
  addDoc,
  where,
  updateDoc,
  doc,
} from "firebase/firestore";
import { db } from "../firebase";
import { apps } from "../data/apps";
import { isLiveOnPlay, ratingLabel, installsLabel, PORTFOLIO } from "../data/appStats";
import "../App.css";

type BlogSummary = {
  id: string;
  title: string;
  text: string;
  category: string;
  createdAt: number;
  isPremium: boolean;
};

// Document faces: Literata (the rendered page) vs IBM Plex Mono (the source).
const fontLink = document.createElement("link");
fontLink.href =
  "https://fonts.googleapis.com/css2?family=Literata:ital,opsz,wght@0,7..72,400;0,7..72,500;0,7..72,600;0,7..72,700;1,7..72,400;1,7..72,600&family=IBM+Plex+Mono:wght@400;500;600&family=IBM+Plex+Sans:wght@400;500;600&display=swap";
fontLink.rel = "stylesheet";
if (!document.querySelector(`link[href="${fontLink.href}"]`)) {
  document.head.appendChild(fontLink);
}

// Only apps actually installable from Play are listed publicly.
//
// Homepage-only: the app being teased above the fold must not be named anywhere on this page —
// rows, table, source view, JSON-LD. Its registry entry (old id "mdpilot", the renamed one
// "agentpager") stays exactly as it is for every other route.
const TEASED_IDS = ["agentpager", "mdpilot"];
const liveApps = apps.filter((a: any) => isLiveOnPlay(a.id) && !TEASED_IDS.includes(a.id));

// Flagship first, then the best-rated apps with broad appeal.
const FEATURED_IDS = ["mdvedit", "webeva", "pdftoolkitai", "htmlvedit", "sqlvedit", "pyvedit"];
const orderedApps = [
  ...FEATURED_IDS.map((id) => liveApps.find((a: any) => a.id === id)).filter(Boolean),
  ...liveApps.filter((a: any) => !FEATURED_IDS.includes(a.id)),
] as any[];

// Flagship: Markdown Editor & MD Viewer (mdvedit) — the site's prime app.
// ⭐ THE TWO NEWEST APPS, given their own band above the table.
//
// 🧨 `isFeatured` in apps.js renders NOTHING — nothing on this site reads it,
// and it has been sitting on webeva and hostpilot doing nothing at all. A flag
// that looks like it promotes an app and does not is worse than no flag,
// because it gets set and then trusted. This list is what actually shows.
//
// They earn a band rather than a row because a new app has no rating and no
// install count yet, and the table ranks on exactly those two things — so the
// newest work would sit at the bottom looking like the weakest.
const NEW_IDS = ["aspenote", "hostpilot"];
const newApps = NEW_IDS.map((id) => liveApps.find((a: any) => a.id === id)).filter(Boolean) as any[];

const flagship = liveApps.find((a: any) => a.id === "mdvedit");
// Play install-referrer UTM so hero installs are attributable in GA4.
const FLAGSHIP_PLAY_URL =
  "https://play.google.com/store/apps/details?id=com.cosmovex.mdvedit&referrer=" +
  encodeURIComponent("utm_source=cosmovexapps&utm_medium=home_hero&utm_campaign=mdvedit");

const FLAGSHIP_FAQ = [
  {
    q: "Is there a free Markdown editor for Android?",
    a: "Yes — Markdown Editor & MD Viewer is free on Google Play. It gives you a split-view editor with live preview, syntax highlighting, and HTML/PDF export, and it works fully offline with no account.",
  },
  {
    q: "Can I edit Markdown from my phone on a desktop browser?",
    a: "Markdown Editor & MD Viewer includes a Live Server: your phone hosts the document over Wi-Fi and you edit it from any desktop browser with two-way live sync. The file never leaves your device.",
  },
  {
    q: "Can I make Mermaid diagrams on Android?",
    a: "Yes — the built-in Mermaid Studio turns plain text into flowcharts, sequence and class diagrams, with pan/zoom and SVG export, right on your phone.",
  },
  {
    q: "Can I turn Markdown into slides or a PDF on my phone?",
    a: "Presentation Mode turns any Markdown document into a fullscreen slide deck, and export produces styled HTML or print-ready PDF — all generated on-device.",
  },
];

const V15_FEATURES = [
  ["Live Server", "edit this phone's document from any desktop browser over Wi-Fi, two-way sync"],
  ["Mermaid Studio", "flowcharts, sequence & class diagrams from plain text — pan, zoom, export SVG"],
  ["Presentation Mode", "any document becomes a fullscreen slide deck"],
  ["Styled export", "HTML and print-ready PDF with page numbers, generated on-device"],
  ["Version History", "every save snapshotted; browse and restore"],
  ["Works offline", "no account, no cloud — files stay on your device"],
];

// The first four features play themselves on the stage; the last two are facts.
const SCENE_COUNT = 4;

// ── Feature theater scenes (pure CSS, decorative; text lives in the list) ──
const LiveScene = () => (
  <div className="sc sc-live" aria-hidden="true">
    <div className="sc-frame">
      <div className="sc-bar"><span className="sc-tag amb-bg">phone</span> roadmap.md</div>
      <div className="sc-body"><span className="sc-type">Ship v1.5 today ✨</span><span className="sc-cur amb-bg" /></div>
    </div>
    <div className="sc-sync">⇄</div>
    <div className="sc-frame">
      <div className="sc-bar"><span className="dotr" /><span className="doty" /><span className="dotg" /> 192.168.1.7:8482</div>
      <div className="sc-body"><span className="sc-type">Ship v1.5 today ✨</span><span className="sc-cur blu-bg" /></div>
    </div>
  </div>
);

const MermaidScene = () => (
  <div className="sc sc-mm" aria-hidden="true">
    <div className="sc-code">
      <span className="sc-line sc-l1">graph LR</span>
      <span className="sc-line sc-l2">  Idea --&gt; Build --&gt; Ship</span>
    </div>
    <div className="sc-mmout">
      <span className="mm-node sc-n1">Idea</span>
      <span className="sc-arr sc-a1" />
      <span className="mm-node sc-n2">Build</span>
      <span className="sc-arr sc-a2" />
      <span className="mm-node mm-hot sc-n3">Ship</span>
    </div>
  </div>
);

const SlidesScene = () => (
  <div className="sc sc-sl" aria-hidden="true">
    <div className="sc-deck">
      <div className="sc-slide sc-s1"><b># Q3 Goals</b><span>press play on your doc</span></div>
      <div className="sc-slide sc-s2"><b>## Ship v1.5</b><span>slides straight from markdown</span></div>
      <div className="sc-slide sc-s3"><b>## Thank you</b><span>— made on a phone</span></div>
    </div>
    <div className="sc-prog"><span /></div>
  </div>
);

const PdfScene = () => (
  <div className="sc sc-pdf" aria-hidden="true">
    <div className="sc-page">
      <b>Q3 Roadmap</b>
      <span className="sc-pl" /><span className="sc-pl" /><span className="sc-pl sc-pl-s" />
      <div className="sc-pfoot">1 / 3</div>
    </div>
    <span className="sc-badge">PDF ✓</span>
  </div>
);

// Play click → GA conversion event, then store.
const playClick = (e: React.MouseEvent, url: string, appId: string, source: string) => {
  e.preventDefault();
  e.stopPropagation();
  try {
    const w = window as any;
    w.gtag?.("event", "app_cta_click", { app_id: appId, source });
    if (w.gtag_report_conversion) { w.gtag_report_conversion(url); return; }
  } catch {}
  window.open(url, "_blank", "noopener");
};

// The page, authored as the markdown it claims to be. Shown by the Source flip.
const buildSourceMd = (blogs: BlogSummary[], toolNames: string[]) => {
  const appRows = orderedApps
    .map((a: any) => {
      const r = ratingLabel(a.id);
      return `| [${a.name}](/apps/${a.id}) | ${a.tagline} | ${r ? r.rating.toFixed(1) + "★" : "—"} |`;
    })
    .join("\n");
  const faq = FLAGSHIP_FAQ.map((f) => `### ${f.q}\n\n${f.a}`).join("\n\n");
  const posts = blogs.map((b) => `- [${b.title}](/blogs)`).join("\n") || "- (loading journal…)";
  return `# Your work keeps moving. So should you.

> untitled.md — unsaved. Built for the hours spent watching a terminal wait.
> Something new from this studio is nearly ready. It is not an app you can name yet.

---

# A Markdown editor good enough to write its own homepage.

This page is a Markdown document. You are reading the **rendered preview**;
you just flipped to the **source**. That round trip — plain text in, a clean
document out — is what [Markdown Editor & MD Viewer](${FLAGSHIP_PLAY_URL})
does on your phone, free.

> ⚡ Live Server: turn it on and this phone's document opens in your
> desktop browser over Wi-Fi — both sides editing, synced live.
> The file never leaves the device.

[**▶ Install free on Google Play**](${FLAGSHIP_PLAY_URL}) — ★ 4.7 · 10,000+ installs

## New in v1.5

${V15_FEATURES.map(([t, d]) => `- [x] **${t}** — ${d}`).join("\n")}

## Diagrams from plain text

\`\`\`mermaid
graph LR
  Idea --> Build --> Ship
\`\`\`

Mermaid Studio renders this on-device — pan, zoom, export as SVG.

## The rest of the toolkit

One app per job. No 200 MB do-everything bloat.

| App | Job | Rating |
|---|---|---|
${appRows}

## Free web tools

${toolNames.map((t) => `\`${t}\``).join(" · ") || "`Background Remover` · `Snapglow` · `SQLite Viewer`"}

## Questions, answered

${faq}

## From the journal

${posts}

---

*Cosmovex builds focused, offline-first developer apps.
No accounts. No bloat. [About](/about) · [Contact](/contact) ·
[Privacy](/privacy-policy) · [Terms](/terms-of-service)*
`;
};

// Minimal per-line highlighter for the Source view. Content is self-authored.
const SrcLine: React.FC<{ line: string }> = ({ line }) => {
  let cls = "src-t";
  if (/^#{1,3} /.test(line)) cls = "src-h";
  else if (/^> ?/.test(line)) cls = "src-q";
  else if (/^```/.test(line)) cls = "src-f";
  else if (/^\|/.test(line)) cls = "src-tb";
  else if (/^- /.test(line)) cls = "src-li";
  else if (/^---/.test(line)) cls = "src-hr";
  // Linkify [text](url); bold **text**. Split conservatively.
  const parts: React.ReactNode[] = [];
  let rest = line;
  let key = 0;
  const linkRe = /\[([^\]]+)\]\(([^)]+)\)/;
  while (rest.length) {
    const m = rest.match(linkRe);
    if (!m || m.index === undefined) { parts.push(<span key={key++}>{rest}</span>); break; }
    if (m.index > 0) parts.push(<span key={key++}>{rest.slice(0, m.index)}</span>);
    const href = m[2];
    parts.push(
      href.startsWith("/") ? (
        <Link key={key++} className="src-a" to={href}>[{m[1]}]</Link>
      ) : (
        <a key={key++} className="src-a" href={href} rel="noopener noreferrer">[{m[1]}]</a>
      )
    );
    rest = rest.slice(m.index + m[0].length);
  }
  return <div className={`src-ln ${cls}`}>{parts}</div>;
};

const LINE_H = 27; // px — one document "line" for gutter + status-bar math

const CosmovexLanding: React.FC = () => {
  // Opens in preview everywhere while the teaser is the first viewport — a split editor would hand
  // half the screen to source lines before a visitor has read a word. Split is still one click away.
  const [view, setView] = useState<"preview" | "source" | "split">("preview");
  const splitSrcRef = useRef<HTMLElement | null>(null);
  const [mobileNavOpen, setMobileNavOpen] = useState(false);
  const [latestBlogs, setLatestBlogs] = useState<BlogSummary[]>([]);
  const [newsletterEmail, setNewsletterEmail] = useState("");
  const [newsletterStatus, setNewsletterStatus] = useState<"idle" | "loading" | "success" | "error">("idle");
  const [topLine, setTopLine] = useState(1);
  const [typed, setTyped] = useState(0);
  const [scene, setScene] = useState(0);
  const scenePaused = useRef(false);
  const [booting, setBooting] = useState(() => {
    try {
      if ((navigator as any).webdriver) return false; // prerender/crawlers: skip
      if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return false;
      return true;
    } catch { return false; }
  });

  // Boot: a breath of raw source, then the render sweep. Decorative only —
  // the real content is in the DOM underneath from the first paint.
  useEffect(() => {
    if (!booting) return;
    const t = setTimeout(() => setBooting(false), 1500);
    return () => clearTimeout(t);
  }, [booting]);

  // Line-number gutter + status bar track scroll like an editor.
  useEffect(() => {
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => setTopLine(Math.max(1, Math.floor(window.scrollY / LINE_H) + 1)));
    };
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => { window.removeEventListener("scroll", onScroll); cancelAnimationFrame(raf); };
  }, []);

  // The "desktop" cursor in the hero fence types alongside you (Live Server).
  const DESK_LINE = "and my desktop is editing this line right now.";
  useEffect(() => {
    try { if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) { setTyped(DESK_LINE.length); return; } } catch {}
    const iv = setInterval(() => {
      setTyped((n) => (n >= DESK_LINE.length + 14 ? 0 : n + 1)); // +14 = hold, then retype
    }, 90);
    return () => clearInterval(iv);
  }, []);

  // Split view: the source pane scroll-follows the preview, like a real editor.
  useEffect(() => {
    if (view !== "split") return;
    const el = splitSrcRef.current;
    if (!el) return;
    let raf = 0;
    const sync = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const max = document.documentElement.scrollHeight - window.innerHeight;
        const p = max > 0 ? window.scrollY / max : 0;
        el.scrollTop = p * (el.scrollHeight - el.clientHeight);
      });
    };
    window.addEventListener("scroll", sync, { passive: true });
    sync();
    return () => { window.removeEventListener("scroll", sync); cancelAnimationFrame(raf); };
  }, [view]);

  // Leave split gracefully when the window gets narrow.
  useEffect(() => {
    const onResize = () => { if (window.innerWidth < 1280) setView((v) => (v === "split" ? "preview" : v)); };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);

  // Feature theater auto-advances until the reader picks a scene themselves.
  useEffect(() => {
    try { if (window.matchMedia("(prefers-reduced-motion: reduce)").matches) return; } catch {}
    const iv = setInterval(() => {
      if (!scenePaused.current && !document.hidden) setScene((s) => (s + 1) % SCENE_COUNT);
    }, 6500);
    return () => clearInterval(iv);
  }, []);

  // Backtick flips Source ⇄ Preview, like an editor.
  const flip = useCallback(() => setView((v) => (v === "preview" ? "source" : "preview")), []);
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const t = e.target as HTMLElement;
      if (t && (t.tagName === "INPUT" || t.tagName === "TEXTAREA")) return;
      if (e.key === "`") { e.preventDefault(); flip(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [flip]);

  useEffect(() => {
    const fetchLatestBlogs = async () => {
      try {
        const blogsQuery = query(collection(db, "Blogs"), orderBy("createdAt", "desc"), limit(3));
        const snapshot = await getDocs(blogsQuery);
        const blogs = snapshot.docs.map((d) => ({
          id: d.id,
          title: d.data().blog_title || "",
          text: d.data().blog_contetnt,
          category: d.data().blog_category || "Uncategorized",
          createdAt: d.data().createdAt || Date.now(),
          isPremium: d.data().isPremium || false,
        }));
        setLatestBlogs(blogs);
      } catch (error) {
        console.error("Error fetching blogs:", error);
      }
    };
    fetchLatestBlogs();
  }, []);


  const handleNewsletterSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newsletterEmail.trim()) return;
    setNewsletterStatus("loading");
    try {
      const usersRef = collection(db, "Users");
      const q = query(usersRef, where("email", "==", newsletterEmail.trim()));
      const userSnapshot = await getDocs(q);
      if (!userSnapshot.empty) {
        const userDoc = userSnapshot.docs[0];
        await updateDoc(doc(db, "Users", userDoc.id), {
          subscribedToNewsletter: true,
          subscriptionSource: "landing_page",
          subscribedAt: Date.now(),
          isUnsubscribed: false,
        });
      } else {
        await addDoc(collection(db, "Users"), {
          email: newsletterEmail.trim(),
          displayName: "",
          subscribedToNewsletter: true,
          subscriptionSource: "landing_page",
          subscribedAt: Date.now(),
          unsubscribeToken: crypto.randomUUID(),
          isUnsubscribed: false,
          totalComments: 0,
          totalLikes: 0,
          lastActivityAt: Date.now(),
          createdAt: Date.now(),
        });
      }
      setNewsletterStatus("success");
      setNewsletterEmail("");
      setTimeout(() => setNewsletterStatus("idle"), 4000);
    } catch (error) {
      console.error("Error subscribing:", error);
      setNewsletterStatus("error");
      setTimeout(() => setNewsletterStatus("idle"), 3000);
    }
  };

  const generateSlug = (title: string) =>
    (title || "").toLowerCase().replace(/[^\w\s-]/g, "").replace(/\s+/g, "-").replace(/-+/g, "-").substring(0, 60);

  const appsStructuredData = orderedApps.map((app: any) => {
    const r = ratingLabel(app.id);
    return {
      "@type": "MobileApplication",
      name: app.name,
      description: app.description,
      operatingSystem: "Android",
      applicationCategory: "DeveloperApplication",
      offers: { "@type": "Offer", price: "0", priceCurrency: "USD" },
      url: `https://cosmovexapps.com/apps/${app.id}`,
      installUrl: app.playstore,
      ...(r && { aggregateRating: { "@type": "AggregateRating", ratingValue: r.rating, ratingCount: r.count } }),
      author: { "@type": "Organization", name: "Cosmovex", url: "https://cosmovexapps.com" },
    };
  });

  const sourceMd = buildSourceMd(latestBlogs, ["Background Remover", "Snapglow", "SQLite Viewer", "QueryDrill"]);
  const deskShown = DESK_LINE.slice(0, Math.min(typed, DESK_LINE.length));

  return (
    <div className="ed" data-view={view}>
      <style>{ed_css}</style>
      <Helmet>
        <title>Markdown Editor & MD Viewer for Android — Live Preview, Mermaid, PDF | Cosmovex</title>
        <meta name="title" content="Markdown Editor & MD Viewer for Android — Live Preview, Mermaid, PDF | Cosmovex" />
        <meta
          name="description"
          content="Markdown Editor & MD Viewer for Android: live preview, Mermaid diagrams, slide decks, PDF export, and a Live Server that syncs to your desktop browser. Free & offline. Plus 24 more free developer apps and 35+ in-browser tools from Cosmovex."
        />
        <meta
          name="keywords"
          content="markdown editor android, md viewer android, markdown app, mermaid diagram android, markdown to pdf android, markdown to slides, android developer apps, code editor android, python compiler android, java compiler android, ssh client android, json editor, free developer tools, Cosmovex"
        />
        <link rel="canonical" href="https://cosmovexapps.com/" />
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://cosmovexapps.com/" />
        <meta property="og:title" content="Markdown Editor & MD Viewer for Android — Live Preview, Mermaid, PDF | Cosmovex" />
        <meta property="og:description" content="Write Markdown on your phone, edit it from your desktop browser over Wi-Fi, build Mermaid diagrams, present slides, export PDF. Free & offline — plus the full Cosmovex developer toolkit." />
        <meta property="og:site_name" content="Cosmovex" />
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content="https://cosmovexapps.com/" />
        <meta property="twitter:title" content="Markdown Editor & MD Viewer for Android — Live Preview, Mermaid, PDF | Cosmovex" />
        <meta property="twitter:description" content="Write Markdown on your phone, edit from your desktop browser over Wi-Fi, build Mermaid diagrams, export slides & PDF. Free & offline." />
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "FAQPage",
            mainEntity: FLAGSHIP_FAQ.map((f) => ({
              "@type": "Question",
              name: f.q,
              acceptedAnswer: { "@type": "Answer", text: f.a },
            })),
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "WebSite",
            name: "Cosmovex",
            description: "Free Android developer apps and in-browser tools.",
            url: "https://cosmovexapps.com/",
            potentialAction: {
              "@type": "SearchAction",
              target: "https://cosmovexapps.com/blogs?q={search_term_string}",
              "query-input": "required name=search_term_string",
            },
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Organization",
            name: "Cosmovex",
            url: "https://cosmovexapps.com/",
            logo: "https://cosmovexapps.com/logo192.png",
            description: "We build free, privacy-first developer apps and tools.",
            sameAs: [
              "https://play.google.com/store/apps/dev?id=Cosmovex",
              "https://www.linkedin.com/company/cosmovex",
            ],
          })}
        </script>
        <script type="application/ld+json">
          {JSON.stringify({ "@context": "https://schema.org", "@graph": appsStructuredData })}
        </script>
      </Helmet>

      {booting && (
        <div className="ed-boot" aria-hidden="true">
          <div className="ed-boot-in">
            <div className="ed-boot-ln"># Your work keeps moving.</div>
            <div className="ed-boot-ln"># So should you.</div>
            <div className="ed-boot-ln ed-boot-dim">opening untitled.md…</div>
          </div>
        </div>
      )}

      {/* ── Tab bar ── */}
      <header className="ed-tab">
        <Link to="/" className="ed-brand">
          <img src="/logo192.png" alt="" width="20" height="20" /> cosmovex
        </Link>
        <span className="ed-file">home.md <span className="ed-mod" aria-hidden="true">●</span></span>
        <nav className="ed-nav">
          <Link to="/apps">apps</Link>
          <Link to="/articles">guides</Link>
          <Link to="/blogs">journal</Link>
          <Link to="/about">about</Link>
        </nav>
        <div className="ed-right">
          <div className="ed-flip" role="tablist" aria-label="View">
            <button role="tab" aria-selected={view === "preview"} className={view === "preview" ? "on" : ""} onClick={() => setView("preview")}>Preview</button>
            <button role="tab" aria-selected={view === "split"} className={view === "split" ? "on ed-splitbtn" : "ed-splitbtn"} onClick={() => setView("split")}>Split</button>
            <button role="tab" aria-selected={view === "source"} className={view === "source" ? "on" : ""} onClick={() => setView("source")}>Source</button>
          </div>
          <a
            className="ed-get"
            href={FLAGSHIP_PLAY_URL}
            onClick={(e) => playClick(e, FLAGSHIP_PLAY_URL, "mdvedit", "home_tabbar")}
            rel="noopener noreferrer"
          >Get the app</a>
          <button className="ed-burger" onClick={() => setMobileNavOpen((v) => !v)} aria-label="Menu">≡</button>
        </div>
      </header>
      {mobileNavOpen && (
        <div className="ed-mob">
          <Link to="/apps">apps</Link>
          <Link to="/articles">guides</Link>
          <Link to="/blogs">journal</Link>
          <Link to="/about">about</Link>
        </div>
      )}

      {/* ── Line-number gutter (an editor's truth, not decoration) ── */}
      <div className="ed-gutter" aria-hidden="true">
        {Array.from({ length: 48 }, (_, i) => (
          <span key={i}>{topLine + i}</span>
        ))}
      </div>

      {view === "source" && (
        <main className="src" aria-label="This page as Markdown source">
          {sourceMd.split("\n").map((ln, i) => (
            <SrcLine key={i} line={ln} />
          ))}
        </main>
      )}
      {view === "split" && (
        <aside className="split-src" ref={splitSrcRef as any} aria-label="Live Markdown source of this page">
          {sourceMd.split("\n").map((ln, i) => (
            <SrcLine key={i} line={ln} />
          ))}
        </aside>
      )}
      {view !== "source" && (
        <main className={view === "split" ? "doc doc-half" : "doc"}>
          {/* ── untitled.md — the teaser. An unsaved file pinned above the document: nothing named,
                 nothing shown, one feeling. The ribbon is decorative; the words carry it. ── */}
          <section className="tz" aria-labelledby="tz-title">
            <p className="tz-path">~/cosmovex/<b>untitled.md</b> <span className="tz-unsaved" aria-hidden="true">●</span> <span className="tz-unsaved-txt">unsaved</span></p>
            <h1 id="tz-title" className="tz-h1">
              <span className="tz-l1">Your work keeps moving.</span>
              <em className="tz-l2">So should you.</em>
            </h1>
            <p className="tz-lede">
              Built for the hours spent watching a terminal wait.
              Something new from this studio is nearly ready — and it isn't for your desk.
            </p>
            {/* The night dial: 24 hours on a rim. The work-ticks keep lighting up on their own; the
                hollow "you" leaves the rim in the evening and is back in the morning. Pure SVG + CSS. */}
            <figure className="tz-dial" aria-hidden="true">
              <svg viewBox="0 0 400 400" className="tz-svg" focusable="false">
                <circle cx="200" cy="200" r="158" className="tz-rim" />
                {Array.from({ length: 48 }, (_, i) => (
                  <line
                    key={i}
                    className="tz-tick"
                    x1="200" y1="30" x2="200" y2={i % 4 === 0 ? "50" : "44"}
                    transform={`rotate(${i * 7.5} 200 200)`}
                    style={{ ["--i" as any]: i }}
                  />
                ))}
                <circle cx="200" cy="200" r="118" className="tz-inner" />
                <text x="200" y="192" className="tz-ctr tz-ctr-a" textAnchor="middle">the work</text>
                <text x="200" y="216" className="tz-ctr tz-ctr-b" textAnchor="middle">still going</text>
                <text x="20" y="352" className="tz-hr">18:00 — you leave</text>
                <text x="214" y="102" className="tz-hr">07:00 — you're back</text>
                <g className="tz-you-g">
                  <circle cx="200" cy="200" r="9" className="tz-you" transform="translate(-118 106)" />
                </g>
              </svg>
            </figure>


            <div className="tz-cta">
              <a
                className="tz-btn"
                href="#newsletter"
                onClick={() => { try { (window as any).gtag?.("event", "teaser_cta_click", { source: "home_teaser" }); } catch {} }}
              >Be first to hear</a>
              <span className="tz-note">Coming soon. Details land in the studio newsletter — the same one below, nothing extra to join.</span>
            </div>
            <a className="tz-more" href="#document" aria-label="Skip to the rest of the page">Meanwhile, the toolkit <span aria-hidden="true">↓</span></a>

          </section>

          {/* ── The document ── */}
          <p className="doc-path" id="document">~/cosmovex/<b>home.md</b> · rendered with Markdown Editor &amp; MD Viewer</p>
          <h1 className="doc-h1">
            A Markdown editor good enough to <em>write its own homepage.</em>
          </h1>
          <p className="doc-lede">
            This page is a Markdown document. You're reading the rendered preview — press{" "}
            <button className="doc-key" onClick={flip}>`</button> or hit{" "}
            <button className="doc-srcbtn" onClick={flip}>Source</button> if you don't believe it.
            That round trip — plain text in, a clean document out — is what{" "}
            <strong>Markdown Editor &amp; MD Viewer</strong> does on your Android phone. Free, offline, no account.
          </p>

          {/* The live fence: Live Server demoing itself */}
          <figure className="doc-fence">
            <figcaption><span className="fence-lang">markdown</span><span className="fence-live"><span className="live-dot" /> Live Server — 2 editors, 1 file</span></figcaption>
            <pre>
              <span className="src-h"># Two cursors, one document</span>{"\n"}
              <span className="src-t">My phone is hosting this file over Wi-Fi,</span>{"\n"}
              <span className="src-t">{deskShown}</span><span className="cur cur-desk" data-who="desktop" />{"\n"}
              <span className="src-q">&gt; nothing leaves the device — it's your Wi-Fi, your file</span><span className="cur cur-phone" data-who="phone" />
            </pre>
          </figure>

          <div className="doc-cta">
            {flagship && (
              <a
                href={FLAGSHIP_PLAY_URL}
                onClick={(e) => playClick(e, FLAGSHIP_PLAY_URL, "mdvedit", "home_hero")}
                className="btn-install"
                rel="noopener noreferrer"
              >▶&nbsp; Install free on Google Play</a>
            )}
            <span className="doc-proof">★ 4.7 on Play · 10,000+ installs · free</span>
          </div>

          <hr className="doc-hr" />

          {/* v1.5 feature theater: the checklist doubles as tabs; the stage plays each feature */}
          <h2 className="doc-h2"><span className="h-mark">##</span> New in v1.5 — watch it work</h2>
          <div className="ft">
            <div className="ft-list" role="tablist" aria-label="v1.5 features">
              {V15_FEATURES.slice(0, SCENE_COUNT).map(([t, d], i) => (
                <button
                  key={t}
                  role="tab"
                  aria-selected={scene === i}
                  className={scene === i ? "ft-it on" : "ft-it"}
                  onClick={() => { setScene(i); scenePaused.current = true; }}
                >
                  <span className="ft-play">{scene === i ? "▸" : "·"}</span>
                  <span className="ft-txt"><b>{t}</b> — {d}</span>
                </button>
              ))}
              {V15_FEATURES.slice(SCENE_COUNT).map(([t, d]) => (
                <div key={t} className="ft-it ft-static">
                  <span className="ft-play tick">✓</span>
                  <span className="ft-txt"><b>{t}</b> — {d}</span>
                </div>
              ))}
            </div>
            <div className="ft-stage" key={scene}>
              {scene === 0 && <LiveScene />}
              {scene === 1 && <MermaidScene />}
              {scene === 2 && <SlidesScene />}
              {scene === 3 && <PdfScene />}
            </div>
          </div>

          {/* Just shipped — the newest two, before the ranked table */}
          {newApps.length > 0 && (
            <>
              <h2 className="doc-h2"><span className="h-mark">##</span> Just shipped</h2>
              <p className="doc-p">Both offline-first, both free to try.</p>
              <div className="doc-new">
                {newApps.map((a: any) => (
                  <Link key={a.id} to={`/apps/${a.id}`} className="nw" style={{ ["--nw-accent" as any]: a.accentHex }}>
                    <img src={a.icon} alt="" width="46" height="46" loading="lazy" />
                    <div className="nw-body">
                      <span className="nw-new">NEW</span>
                      <b className="nw-name">{a.name}</b>
                      <span className="nw-line">{a.tagline}</span>
                      <span className="nw-feat">{(a.features || []).slice(0, 3).join(" · ")}</span>
                    </div>
                    <span className="nw-go" aria-hidden="true">→</span>
                  </Link>
                ))}
              </div>
            </>
          )}

          {/* Apps table */}
          <h2 className="doc-h2"><span className="h-mark">##</span> The rest of the toolkit</h2>
          <p className="doc-p">One app per job. No 200&nbsp;MB do-everything bloat.</p>
          <div className="doc-tbl-wrap">
            <table className="doc-tbl">
              <thead>
                <tr><th>App</th><th>Job</th><th>Rating</th><th></th></tr>
              </thead>
              <tbody>
                {orderedApps.map((a: any) => {
                  const r = ratingLabel(a.id);
                  const inst = installsLabel(a.id);
                  return (
                    <tr key={a.id} className={a.id === "mdvedit" ? "tbl-flag" : ""}>
                      <td><Link className="tbl-app" to={`/apps/${a.id}`}><img src={a.icon} alt="" width="22" height="22" loading="lazy" />{a.name}</Link></td>
                      <td className="tbl-job">{a.tagline}</td>
                      <td className="tbl-r">{r ? `${r.rating.toFixed(1)}★` : "—"}{inst ? ` · ${inst.replace(/\s*downloads?/i, "")}` : ""}</td>
                      <td className="tbl-get">
                        <a href={a.playstore} onClick={(e) => playClick(e, a.playstore, a.id, "home_table")} rel="noopener noreferrer">install</a>
                        {a.appstore && <a href={a.appstore} onClick={(e) => playClick(e, a.appstore, a.id, "home_table_ios")} rel="noopener noreferrer">ios</a>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          {/* Web tools */}
          <h2 className="doc-h2"><span className="h-mark">##</span> Free web tools</h2>
          <p className="doc-p doc-chips">
            <a className="chip" href="/background-remover">Background Remover</a>
            <a className="chip" href="/snapglow">Snapglow</a>
            <a className="chip" href="/sqlite-viewer">SQLite Viewer</a>
            <a className="chip" href="/querydrill">QueryDrill</a>
          </p>

          {/* FAQ */}
          <h2 className="doc-h2"><span className="h-mark">##</span> Questions, answered</h2>
          {FLAGSHIP_FAQ.map((f) => (
            <details className="doc-faq" key={f.q}>
              <summary>{f.q}</summary>
              <p>{f.a}</p>
            </details>
          ))}

          {/* Journal */}
          {latestBlogs.length > 0 && (
            <>
              <h2 className="doc-h2"><span className="h-mark">##</span> From the journal</h2>
              <ul className="doc-posts">
                {latestBlogs.map((b) => (
                  <li key={b.id}>
                    <Link to={`/blog/${generateSlug(b.title)}`}>{b.title}</Link>
                    <span className="post-cat">{b.category}</span>
                  </li>
                ))}
              </ul>
            </>
          )}

          <blockquote className="doc-quote">
            We're a small studio with one rule: each app does a single job well,
            works offline, and never asks who you are.
          </blockquote>

          {/* Newsletter */}
          <h2 className="doc-h2" id="newsletter"><span className="h-mark">##</span> Stay in the loop</h2>
          <form className="doc-news" onSubmit={handleNewsletterSubmit}>
            <input
              type="email"
              placeholder="you@example.com"
              value={newsletterEmail}
              onChange={(e) => setNewsletterEmail(e.target.value)}
              required
            />
            <button type="submit" disabled={newsletterStatus === "loading"}>
              {newsletterStatus === "loading" ? "…" : "Subscribe"}
            </button>
          </form>
          {newsletterStatus === "success" && <p className="news-ok">Subscribed. See you in the next one.</p>}
          {newsletterStatus === "error" && <p className="news-err">That didn't send — try again.</p>}

          {/* Footnotes */}
          <hr className="doc-hr" />
          <ol className="doc-foot">
            <li><Link to="/apps">All {liveApps.length} apps</Link></li>
            <li><Link to="/articles">Guides</Link></li>
            <li><Link to="/blogs">Journal</Link></li>
            <li><Link to="/about">About</Link></li>
            <li><Link to="/contact">Contact</Link></li>
            <li><Link to="/privacy-policy">Privacy</Link></li>
            <li><Link to="/terms-of-service">Terms</Link></li>
          </ol>
          <p className="doc-eof">© {new Date().getFullYear()} Cosmovex — EOF</p>
        </main>
      )}

      {/* ── Status bar ── */}
      <footer className="ed-status">
        <span>Ln {topLine}, Col 1</span>
        <span className="st-sep" />
        <span>UTF-8</span>
        <span className="st-sep" />
        <span>Markdown</span>
        <span className="st-grow" />
        <span className="st-live"><span className="live-dot" /> Live Server: connected</span>
        <span className="st-sep" />
        <span>★ 4.7 on Play</span>
      </footer>
    </div>
  );
};

const ed_css = `
.ed{--bg:#FBFAF6;--panel:#F1EEE5;--hair:#E1DCCE;--ink:#26313B;--mut:#8A93A2;--amb:#A8720E;--lnk:#0F6BBF;--grn:#2E7D4F;--soft:0 1px 2px rgba(52,44,26,.06),0 4px 14px rgba(52,44,26,.05);
  background:var(--bg);color:var(--ink);min-height:100vh;
  font-family:"IBM Plex Sans",-apple-system,BlinkMacSystemFont,sans-serif;-webkit-font-smoothing:antialiased}
.ed ::selection{background:#A8720E;color:#fff}
.ed a{color:inherit;text-decoration:none}
.ed :focus-visible{outline:2px solid var(--amb);outline-offset:2px;border-radius:3px}

/* boot */
.ed-boot{position:fixed;inset:0;z-index:90;background:var(--bg);display:grid;place-items:center;animation:bootout .5s ease 1s forwards;pointer-events:none}
.ed-boot-in{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:15px;line-height:2}
.ed-boot-ln{color:var(--amb);overflow:hidden;white-space:nowrap;animation:boottype .5s steps(30) both}
.ed-boot-ln:nth-child(2){animation-delay:.35s}
.ed-boot-dim{color:var(--mut);animation-delay:.7s}
@keyframes boottype{from{max-width:0}to{max-width:100%}}
@keyframes bootout{to{opacity:0;visibility:hidden}}

/* tab bar */
.ed-tab{position:sticky;top:0;z-index:40;display:flex;align-items:center;gap:18px;height:52px;padding:0 18px;
  background:rgba(251,250,246,.94);backdrop-filter:blur(10px);border-bottom:1px solid var(--hair);
  font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:13px}
.ed-brand{display:flex;align-items:center;gap:8px;font-weight:600;color:var(--ink)}
.ed-brand img{border-radius:5px}
.ed-file{color:#5B6B7C;border:1px solid var(--hair);border-bottom-color:transparent;background:var(--panel);
  padding:6px 14px;border-radius:7px 7px 0 0;margin-top:14px;align-self:end;box-shadow:inset 0 2px 0 var(--amb)}
.ed-mod{color:var(--amb)}
.ed-nav{display:flex;gap:16px;color:var(--mut);margin-left:6px}
.ed-nav a:hover{color:var(--ink)}
.ed-right{margin-left:auto;display:flex;align-items:center;gap:12px}
.ed-flip{display:flex;border:1px solid var(--hair);border-radius:7px;overflow:hidden;background:#fff}
.ed-flip button{font:inherit;background:transparent;border:0;color:var(--mut);padding:6px 13px;cursor:pointer}
.ed-flip button.on{background:var(--ink);color:#FBFAF6}
.ed-get{background:#E8B44C;color:#221A06;font-weight:600;padding:7px 15px;border-radius:7px;border:1px solid #D9A32E;box-shadow:var(--soft)}
.ed-get:hover{filter:brightness(1.05)}
.ed-burger{display:none;background:none;border:0;color:var(--ink);font-size:22px;cursor:pointer}
.ed-mob{display:flex;flex-direction:column;gap:2px;padding:10px 18px 16px;border-bottom:1px solid var(--hair);
  font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:14px;background:var(--bg);position:sticky;top:52px;z-index:39}
.ed-mob a{padding:8px 0;color:#5B6B7C}

/* gutter */
.ed-gutter{position:fixed;top:52px;bottom:34px;left:0;width:56px;overflow:hidden;z-index:1;
  border-right:1px solid var(--hair);padding-top:10px;text-align:right;
  font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11.5px;line-height:27px;color:#CFC8B6;user-select:none}
.ed-gutter span{display:block;padding-right:12px}

/* document */
.doc{max-width:940px;margin:0 auto;padding:38px 28px 74px;position:relative;z-index:2}

/* split editor — source pane pinned left, preview flows beside it */
.split-src{position:fixed;top:52px;bottom:34px;left:56px;width:37vw;overflow:hidden;z-index:2;
  border-right:1px solid var(--hair);background:#F6F3EA;padding:26px 26px 80px;
  font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;line-height:1.95}
.split-src .src-ln{min-height:0}
.doc-half{margin:0 0 0 calc(37vw + 96px);max-width:900px;padding-right:44px}
.doc-half .doc-tbl-wrap{margin:0}
.ed-splitbtn{display:none}
@media(min-width:1280px){.ed-splitbtn{display:block}}
@media(max-width:1279px){.split-src{display:none}}
.doc-path{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12.5px;color:var(--mut);margin:0 0 18px}
.doc-path b{color:var(--ink);font-weight:600}
.doc-h1{font-family:"Literata",Georgia,serif;font-weight:600;font-size:clamp(32px,5.2vw,50px);line-height:1.1;letter-spacing:-.015em;margin:0 0 18px}
.doc-h1 em{font-style:italic;color:var(--amb)}
.doc-lede{font-family:"Literata",Georgia,serif;font-size:17.5px;line-height:1.7;color:#46566A;margin:0 0 22px}
.doc-lede strong{color:var(--ink)}
.doc-key{font:600 13px "IBM Plex Mono",monospace;color:var(--amb);background:#fff;border:1px solid var(--hair);
  border-bottom-width:2px;border-radius:5px;padding:1px 8px;cursor:pointer;vertical-align:1px}
.doc-srcbtn{font:inherit;color:var(--lnk);background:none;border:0;padding:0;cursor:pointer;text-decoration:underline;text-underline-offset:3px}
.doc-p{font-family:"Literata",Georgia,serif;font-size:16px;line-height:1.7;color:#46566A;margin:0 0 14px}
.doc-cap{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;color:var(--mut);margin:8px 0 0}

/* fences */
.doc-fence{border:1px solid var(--hair);border-radius:9px;background:#fff;margin:0 0 20px;overflow:hidden;box-shadow:var(--soft)}
.doc-fence figcaption{display:flex;justify-content:space-between;align-items:center;padding:7px 14px;border-bottom:1px solid var(--hair);
  background:var(--panel);font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11.5px;color:#5B6B7C}
.fence-live{color:var(--grn);display:inline-flex;align-items:center;gap:7px}
.live-dot{width:7px;height:7px;border-radius:50%;background:var(--grn);box-shadow:0 0 6px rgba(46,125,79,.6);display:inline-block;animation:pulse 1.8s ease infinite}
@keyframes pulse{50%{opacity:.45}}
.doc-fence pre{margin:0;padding:13px 16px;font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:13.5px;line-height:2.5;overflow-x:auto}
.doc-fence-sm pre{font-size:12.5px;line-height:1.8}
.src-h{color:var(--amb);font-weight:600}
.src-t{color:#4A5A6B}
.src-q{color:var(--grn);font-style:italic}
.src-f{color:var(--lnk)}
.src-tb{color:#77879A}
.src-li{color:#3D4B59}
.src-hr{color:var(--mut)}
.cur{display:inline-block;width:8px;height:16px;vertical-align:-3px;margin-left:1px;position:relative;animation:blink 1.1s steps(1) infinite}
.cur-desk{background:var(--lnk)}
.cur-phone{background:var(--amb);animation-delay:.4s}
.cur::after{content:attr(data-who);position:absolute;top:-16px;left:0;font-size:8.5px;font-weight:600;letter-spacing:.04em;line-height:1.3;
  font-family:"IBM Plex Mono",monospace;padding:1px 5px;border-radius:4px;color:#fff;white-space:nowrap;pointer-events:none}
.cur-desk::after{background:var(--lnk)}
.cur-phone::after{background:var(--amb)}
@keyframes blink{50%{opacity:0}}
@media(prefers-reduced-motion:reduce){.cur,.live-dot,.ed-boot-ln{animation:none}}

/* cta */
.doc-cta{display:flex;align-items:center;gap:20px;flex-wrap:wrap;margin:0 0 6px}
.btn-install{font-family:"IBM Plex Mono",ui-monospace,monospace;font-weight:700;font-size:14.5px;background:#E8B44C;color:#221A06;
  padding:13px 22px;border-radius:8px;border:1px solid #D9A32E;transition:.15s;box-shadow:var(--soft)}
.btn-install:hover{filter:brightness(1.05);transform:translateY(-1px)}
.doc-proof{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12.5px;color:#5B6B7C}
.doc-hr{border:0;border-top:1px solid var(--hair);margin:28px 0}

/* headings */
.doc-h2{font-family:"Literata",Georgia,serif;font-weight:600;font-size:clamp(23px,3.2vw,29px);letter-spacing:-.01em;margin:34px 0 12px}
.h-mark{color:var(--amb);opacity:.6;font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:.72em;font-weight:500;margin-right:12px;vertical-align:2px;letter-spacing:3px}

/* feature theater */
.tick{color:var(--grn);font-family:"IBM Plex Mono",monospace;font-weight:600;flex:none}
.ft{display:grid;grid-template-columns:1fr 1fr;gap:18px;align-items:stretch}
.ft-list{display:flex;flex-direction:column}
.ft-it{font-family:"Literata",Georgia,serif;font-size:15px;line-height:1.5;color:#46566A;text-align:left;
  background:none;border:0;border-bottom:1px dashed var(--hair);border-left:2px solid transparent;
  padding:9px 10px;display:flex;gap:10px;align-items:baseline;cursor:pointer;transition:.14s}
.ft-it b{color:var(--ink);font-weight:600}
.ft-it:hover{background:#F6F3EA}
.ft-it.on{border-left-color:var(--amb);background:#FAF3E2}
.ft-it.ft-static{cursor:default}
.ft-it.ft-static:hover{background:none}
.ft-play{font-family:"IBM Plex Mono",monospace;font-weight:600;color:var(--amb);flex:none;width:12px}
.ft-txt{flex:1}
.ft-stage{border:1px solid var(--hair);border-radius:9px;background:#fff;box-shadow:var(--soft);
  display:grid;place-items:center;padding:20px;min-height:250px;overflow:hidden}

/* scenes — shared bits */
.sc{width:100%;display:flex;flex-direction:column;align-items:center;gap:12px}
.sc-tag{font-size:8.5px;font-weight:600;color:#fff;border-radius:4px;padding:1px 5px;font-family:"IBM Plex Mono",monospace}
.amb-bg{background:var(--amb)}
.blu-bg{background:var(--lnk)}
.dotr,.doty,.dotg{width:7px;height:7px;border-radius:50%;display:inline-block}
.dotr{background:#E77}
.doty{background:#EC5}
.dotg{background:#7C7}

/* scene: live server */
.sc-live{flex-direction:row;gap:10px;justify-content:center;flex-wrap:wrap}
.sc-frame{border:1px solid var(--hair);border-radius:9px;background:var(--bg);min-width:170px;overflow:hidden;box-shadow:var(--soft)}
.sc-bar{display:flex;align-items:center;gap:5px;padding:6px 9px;border-bottom:1px solid var(--hair);background:var(--panel);
  font-family:"IBM Plex Mono",monospace;font-size:9.5px;color:#5B6B7C}
.sc-body{padding:12px 11px;font-family:"IBM Plex Mono",monospace;font-size:11.5px;color:#3D4B59;white-space:nowrap}
.sc-type{display:inline-block;overflow:hidden;vertical-align:bottom;max-width:0;animation:sctype 2.2s steps(20) .3s forwards}
@keyframes sctype{to{max-width:100%}}
.sc-cur{display:inline-block;width:6px;height:12px;vertical-align:-2px;margin-left:1px;animation:blink 1.1s steps(1) infinite}
.sc-sync{font-size:17px;color:var(--amb);animation:pulse 1.8s ease infinite}

/* scene: mermaid */
.sc-code{border:1px solid var(--hair);border-radius:8px;background:var(--panel);padding:9px 13px;display:flex;flex-direction:column;
  font-family:"IBM Plex Mono",monospace;font-size:11.5px;color:var(--lnk)}
.sc-line{display:inline-block;overflow:hidden;white-space:nowrap;max-width:0}
.sc-l1{animation:sctype .6s steps(8) .2s forwards}
.sc-l2{animation:sctype 1s steps(22) .8s forwards}
.sc-mmout{display:flex;align-items:center;gap:8px;flex-wrap:wrap;justify-content:center}
.sc-n1,.sc-n2,.sc-n3{opacity:0;transform:scale(.7);animation:scpop .35s ease forwards}
.sc-n1{animation-delay:1.9s}
.sc-n2{animation-delay:2.5s}
.sc-n3{animation-delay:3.1s}
@keyframes scpop{to{opacity:1;transform:scale(1)}}
.sc-arr{height:2px;background:var(--mut);width:0;border-radius:2px;animation:scgrow .4s ease forwards}
.sc-a1{animation-delay:2.2s}
.sc-a2{animation-delay:2.8s}
@keyframes scgrow{to{width:26px}}
.mm-node{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12.5px;font-weight:600;color:var(--ink);
  border:1px solid var(--hair);background:var(--panel);border-radius:7px;padding:8px 14px}
.mm-hot{background:var(--amb);color:#fff;border-color:var(--amb)}

/* scene: slides */
.sc-deck{position:relative;width:min(300px,90%);aspect-ratio:16/9}
.sc-slide{position:absolute;inset:0;border:1px solid var(--hair);border-radius:10px;background:var(--bg);box-shadow:var(--soft);
  display:flex;flex-direction:column;align-items:center;justify-content:center;gap:6px;opacity:0;transform:translateX(28px)}
.sc-slide b{font-family:"Literata",Georgia,serif;font-size:17px;color:var(--ink)}
.sc-slide span{font-family:"IBM Plex Mono",monospace;font-size:10px;color:#5B6B7C}
.sc-s1{animation:scslide 6s ease .2s infinite}
.sc-s2{animation:scslide 6s ease 2.2s infinite}
.sc-s3{animation:scslide 6s ease 4.2s infinite}
@keyframes scslide{0%{opacity:0;transform:translateX(28px)}5%,30%{opacity:1;transform:none}38%,100%{opacity:0;transform:translateX(-28px)}}
.sc-prog{width:min(300px,90%);height:3px;background:var(--panel);border-radius:2px;overflow:hidden}
.sc-prog span{display:block;height:100%;background:var(--amb);width:0;animation:scgrowfull 6s linear infinite}
@keyframes scgrowfull{to{width:100%}}

/* scene: pdf */
.sc-pdf{position:relative}
.sc-page{width:170px;aspect-ratio:1/1.35;background:#fff;border:1px solid var(--hair);border-radius:6px;box-shadow:0 10px 30px rgba(52,44,26,.14);
  padding:18px 16px;display:flex;flex-direction:column;gap:9px;transform:translateY(16px);opacity:0;animation:scrise .6s ease .2s forwards}
@keyframes scrise{to{transform:none;opacity:1}}
.sc-page b{font-family:"Literata",Georgia,serif;font-size:13px;color:var(--ink)}
.sc-pl{height:5px;border-radius:3px;background:var(--panel);width:100%}
.sc-pl-s{width:60%}
.sc-pfoot{margin-top:auto;text-align:center;font-family:"IBM Plex Mono",monospace;font-size:8.5px;color:#8A93A2;
  border-top:1px solid var(--hair);padding-top:7px;opacity:0;animation:scpop .4s ease 1.3s forwards}
.sc-badge{position:absolute;top:-7px;right:-16px;font-family:"IBM Plex Mono",monospace;font-size:10.5px;font-weight:700;color:#fff;
  background:var(--grn);border-radius:6px;padding:4px 8px;opacity:0;transform:scale(.7);animation:scpop .35s ease 1.8s forwards}
@media(prefers-reduced-motion:reduce){
  .sc-type,.sc-line{animation:none;max-width:100%}
  .sc-n1,.sc-n2,.sc-n3,.sc-pfoot,.sc-badge{animation:none;opacity:1;transform:none}
  .sc-arr{animation:none;width:26px}
  .sc-page{animation:none;transform:none;opacity:1}
  .sc-s1{animation:none;opacity:1;transform:none}
  .sc-s2,.sc-s3{animation:none}
  .sc-prog span,.sc-sync,.sc-cur{animation:none}
}

/* apps table — breaks out of the prose column so all four columns breathe */
.doc-tbl-wrap{overflow-x:auto;border:1px solid var(--hair);border-radius:9px;margin:0 -70px;background:#fff;box-shadow:var(--soft)}
@media(max-width:1100px){.doc-tbl-wrap{margin:0}}
.doc-tbl{width:100%;border-collapse:collapse;font-size:13.5px}
.doc-tbl th{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11px;font-weight:600;letter-spacing:.05em;text-transform:uppercase;
  color:#5B6B7C;text-align:left;padding:10px 14px;border-bottom:1px solid var(--hair);background:var(--panel)}
.doc-tbl td{padding:9px 14px;border-bottom:1px solid #F0EDE3;vertical-align:middle}
.doc-tbl tr:last-child td{border-bottom:0}
.doc-tbl tr:hover td{background:#FBF7EC}
.tbl-flag td{background:#FAF3E2}
.tbl-app{display:flex;align-items:center;gap:10px;font-weight:600;color:var(--ink);white-space:nowrap}
.tbl-app img{border-radius:6px}
.tbl-app:hover{color:var(--amb)}
.tbl-job{color:#5B6B7C;min-width:200px}
.tbl-r{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;color:#5B6B7C;white-space:nowrap}
.tbl-get{white-space:nowrap}
.tbl-get a{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;font-weight:600;color:var(--lnk);margin-right:12px}
.tbl-get a:hover{text-decoration:underline;text-underline-offset:3px}

/* just shipped */
.doc-new{display:grid;gap:12px;grid-template-columns:repeat(auto-fit,minmax(320px,1fr));margin:2px 0 6px}
.nw{display:flex;align-items:center;gap:14px;padding:16px 18px;background:#fff;border:1px solid var(--hair);
  border-radius:11px;box-shadow:var(--soft);text-decoration:none;color:var(--ink);transition:.16s;position:relative;overflow:hidden}
.nw:before{content:"";position:absolute;inset:0 auto 0 0;width:3px;background:var(--nw-accent,var(--amb))}
.nw:hover{border-color:var(--nw-accent,var(--amb));transform:translateY(-1px);
  box-shadow:0 1px 2px rgba(52,44,26,.06),0 10px 26px -12px rgba(52,44,26,.28)}
.nw img{border-radius:10px;flex:0 0 auto}
.nw-body{display:flex;flex-direction:column;gap:3px;min-width:0}
.nw-new{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:10px;font-weight:600;letter-spacing:1.1px;
  color:var(--nw-accent,var(--amb))}
.nw-name{font-size:14.5px;font-weight:600;line-height:1.25;overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nw-line{font-size:13px;color:#5B6B7C;line-height:1.35}
.nw-feat{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11px;color:#8A93A2;margin-top:2px;
  overflow:hidden;text-overflow:ellipsis;white-space:nowrap}
.nw-go{margin-left:auto;color:var(--nw-accent,var(--amb));font-size:16px;flex:0 0 auto}
@media(max-width:520px){.nw{padding:14px}.nw-feat{display:none}}

/* chips */
.doc-chips{display:flex;flex-wrap:wrap;gap:8px}
.chip{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12.5px;color:#3D4B59;border:1px solid var(--hair);background:#fff;
  border-radius:7px;padding:6px 12px;transition:.14s;box-shadow:var(--soft)}
.chip:hover{border-color:var(--amb);color:var(--amb)}
.chip-more{color:var(--lnk);border-style:dashed;box-shadow:none}

/* faq */
.doc-faq{border-bottom:1px dashed var(--hair)}
.doc-faq summary{font-family:"Literata",Georgia,serif;font-size:16.5px;font-weight:600;padding:13px 0;cursor:pointer;list-style:none;position:relative;padding-right:30px}
.doc-faq summary::after{content:"+";position:absolute;right:4px;color:var(--amb);font-family:"IBM Plex Mono",monospace;font-weight:400}
.doc-faq[open] summary::after{content:"−"}
.doc-faq p{font-family:"Literata",Georgia,serif;font-size:15px;line-height:1.7;color:#46566A;margin:0 0 14px}

/* journal */
.doc-posts{list-style:none;margin:0;padding:0}
.doc-posts li{display:flex;align-items:baseline;justify-content:space-between;gap:14px;padding:9px 0;border-bottom:1px dashed var(--hair)}
.doc-posts a{font-family:"Literata",Georgia,serif;font-size:16px;color:var(--lnk)}
.doc-posts a:hover{text-decoration:underline;text-underline-offset:3px}
.post-cat{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11px;color:var(--mut);flex:none;text-transform:lowercase}

/* quote */
.doc-quote{font-family:"Literata",Georgia,serif;font-style:italic;font-size:18px;line-height:1.65;color:#3D4B59;
  border-left:3px solid var(--amb);margin:28px 0;padding:4px 0 4px 22px}

/* newsletter */
.doc-news{display:flex;gap:10px;flex-wrap:wrap}
.doc-news input{flex:1;min-width:240px;background:#fff;border:1px solid var(--hair);border-radius:8px;color:var(--ink);
  padding:12px 15px;font-size:15px;font-family:"IBM Plex Mono",ui-monospace,monospace}
.doc-news input::placeholder{color:#AEB6C2}
.doc-news button{font-family:"IBM Plex Mono",ui-monospace,monospace;font-weight:700;font-size:14px;background:#E8B44C;color:#221A06;
  border:1px solid #D9A32E;border-radius:8px;padding:12px 22px;cursor:pointer;box-shadow:var(--soft)}
.doc-news button:hover{filter:brightness(1.05)}
.news-ok{color:var(--grn);font-family:"IBM Plex Mono",monospace;font-size:13px;margin-top:10px}
.news-err{color:#C24E4E;font-family:"IBM Plex Mono",monospace;font-size:13px;margin-top:10px}

/* footnotes */
.doc-foot{margin:0;padding:0 0 0 22px;columns:3;font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:13px;color:var(--mut);line-height:2.1}
.doc-foot a{color:#4A5A6B}
.doc-foot a:hover{color:var(--amb)}
.doc-foot li::marker{color:var(--amb)}
.doc-eof{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;color:#AEB6C2;margin-top:24px}

/* source view */
.src{max-width:860px;margin:0 auto;padding:38px 28px 74px;font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:13.5px;line-height:2;position:relative;z-index:2}
.src-ln{white-space:pre-wrap;word-break:break-word;min-height:27px}
.src-ln.src-h{color:var(--amb);font-weight:600}
.src-ln.src-q{color:var(--grn);font-style:italic}
.src-ln.src-f{color:var(--lnk)}
.src-ln.src-tb{color:#77879A}
.src-ln.src-li{color:#3D4B59}
.src-ln.src-hr{color:var(--mut)}
.src-ln.src-t{color:#4A5A6B}
.src-a{color:var(--lnk);text-decoration:underline;text-underline-offset:3px}

/* ── untitled.md: the teaser ─────────────────────────────────────────────────────────────── */
.tz{min-height:calc(100svh - 52px - 34px);display:grid;grid-template-columns:minmax(0,1fr) auto;column-gap:48px;align-content:center;
  grid-template-areas:"path dial" "h1 dial" "lede dial" "cta dial" "more dial";
  padding:6px 0 44px;margin:0 0 30px;border-bottom:1px solid var(--hair);position:relative}
@media(min-width:1101px){.tz{margin-left:-70px;margin-right:-70px}}
.tz-path{grid-area:path}.tz-h1{grid-area:h1}.tz-lede{grid-area:lede}.tz-cta{grid-area:cta;align-self:start}.tz-more{grid-area:more}.tz-dial{grid-area:dial}
.tz-path{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12.5px;color:var(--mut);margin:0 0 26px;display:flex;align-items:center;gap:7px}
.tz-path b{color:var(--ink);font-weight:600}
.tz-unsaved{color:var(--amb)}
.tz-unsaved-txt{color:var(--amb);font-size:11px;letter-spacing:.06em;text-transform:uppercase}
.tz-h1{font-family:"Literata",Georgia,serif;font-weight:600;font-size:clamp(40px,5.6vw,74px);line-height:1.02;letter-spacing:-.022em;
  margin:0 0 26px;max-width:12ch;display:flex;flex-direction:column;gap:.06em}
.tz-l1{color:var(--ink)}
.tz-l2{font-style:italic;color:var(--amb);font-weight:500}
.tz-lede{font-family:"Literata",Georgia,serif;font-size:clamp(17px,2vw,21px);line-height:1.6;color:#46566A;max-width:34ch;margin:0 0 40px}

/* the night dial — ticks light up hour by hour on their own; "you" leaves in the evening and is
   back in the morning. One 14 s loop. Decorative: every word is in the copy column. */
.tz-dial{margin:0;width:min(400px,36vw);align-self:center;filter:drop-shadow(0 18px 30px rgba(52,44,26,.10))}
.tz-svg{width:100%;height:auto;display:block;overflow:visible}
.tz-rim{fill:#FFFDF8;stroke:var(--hair);stroke-width:1}
.tz-inner{fill:none;stroke:var(--hair);stroke-width:1;stroke-dasharray:2 5}
.tz-tick{stroke:#E4DECF;stroke-width:3;stroke-linecap:round;animation:tztick 14s ease-in-out infinite;animation-delay:calc(var(--i) * .2s)}
@keyframes tztick{0%,4%{stroke:#E4DECF}7%{stroke:var(--amb)}12%,70%{stroke:#B7AC8C}78%,100%{stroke:#E4DECF}}
.tz-ctr{font-family:"IBM Plex Mono",ui-monospace,monospace;fill:#5B6B7C;font-size:12px;letter-spacing:.08em;text-transform:uppercase}
.tz-ctr-b{fill:var(--amb);font-weight:600}
.tz-hr{font-family:"IBM Plex Mono",ui-monospace,monospace;fill:#8A93A2;font-size:10.5px}
.tz-you{fill:var(--bg);stroke:var(--ink);stroke-width:1.6}
.tz-you-g{transform-origin:200px 200px;animation:tzorbit 14s ease-in-out infinite}
/* the marker sits at 18:00 (lower-left rim), fades off the rim, then comes back at 07:00 (upper-right) */
@keyframes tzorbit{0%,10%{opacity:1;transform:none}20%,66%{opacity:0;transform:scale(.6)}74%,100%{opacity:1;transform:rotate(195deg)}}

.tz-cta{display:flex;align-items:center;gap:18px;flex-wrap:wrap;margin:0 0 22px}
.ed .tz-btn{font-family:"IBM Plex Mono",ui-monospace,monospace;font-weight:700;font-size:14.5px;color:#FBFAF6;background:var(--ink);
  padding:14px 24px;border-radius:8px;border:1px solid var(--ink);box-shadow:var(--soft);transition:.15s;white-space:nowrap}
.ed .tz-btn:hover{background:#111A22;transform:translateY(-1px)}
.tz-note{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12px;line-height:1.55;color:#5B6B7C;max-width:34ch}
.tz-more{font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:12.5px;color:var(--mut);display:inline-flex;align-items:center;gap:6px;align-self:flex-start}
.tz-more:hover{color:var(--ink)}
.tz-more span{display:inline-block;animation:tznudge 2.4s ease-in-out infinite}
@keyframes tznudge{50%{transform:translateY(3px)}}

/* one entrance: the two lines and the lede settle in once; the ribbon just runs */
.tz-l1,.tz-l2,.tz-lede,.tz-dial,.tz-cta{animation:tzin .7s cubic-bezier(.2,.7,.2,1) both}
.tz-l2{animation-delay:.12s}.tz-lede{animation-delay:.24s}.tz-cta{animation-delay:.36s}.tz-dial{animation-delay:.3s}
@keyframes tzin{from{opacity:0;transform:translateY(10px)}to{opacity:1;transform:none}}

@media(prefers-reduced-motion:reduce){
  .tz-l1,.tz-l2,.tz-lede,.tz-dial,.tz-cta{animation:none}
  .tz-tick{animation:none;stroke:#B7AC8C}
  .tz-you-g{animation:none;transform:rotate(195deg)}
  .tz-more span{animation:none}
}
@media(max-width:820px){
  .tz{min-height:0;padding:4px 0 36px;margin-bottom:24px;grid-template-columns:1fr;column-gap:0;
    grid-template-areas:"path" "h1" "lede" "dial" "cta" "more"}
  .tz-h1{font-size:clamp(38px,11.5vw,54px);max-width:none}
  .tz-lede{font-size:16.5px;margin-bottom:22px}
  .tz-dial{width:min(280px,74vw);justify-self:center;margin:2px 0 26px}
  .tz-cta{gap:12px}
  .tz-btn{width:100%;text-align:center}
  .tz-note{max-width:none}
}
.doc-half .tz{grid-template-columns:1fr;grid-template-areas:"path" "h1" "lede" "dial" "cta" "more";margin-left:0;margin-right:0;min-height:0}
.doc-half .tz-dial{width:min(320px,60%);justify-self:start;margin:6px 0 18px}
.doc-half .tz-h1{font-size:clamp(36px,3.6vw,52px)}
@media(max-width:400px){.tz-h1{font-size:36px}}

/* status bar */
.ed-status{position:fixed;left:0;right:0;bottom:0;z-index:40;display:flex;align-items:center;gap:14px;height:34px;padding:0 16px;
  background:var(--panel);border-top:1px solid var(--hair);
  font-family:"IBM Plex Mono",ui-monospace,monospace;font-size:11.5px;color:#5B6B7C}
.st-sep{width:1px;height:14px;background:var(--hair)}
.st-grow{flex:1}
.st-live{color:var(--grn);display:inline-flex;align-items:center;gap:7px}

@media(max-width:1020px){.ed-gutter{display:none}}
/* Mobile optimizations */
@media(max-width:820px){
  .ed-nav{display:none}.ed-burger{display:block}
  .ed-file{display:none}
  .ed-tab{gap:9px;padding:0 12px}
  .ed-brand{gap:0;font-size:0}
  .ed-right{gap:8px}
  .ed-flip{gap:6px}.ed-flip button{padding:7px 12px;font-size:12.5px;font-weight:600}
  .ed-get{padding:7px 12px;font-size:12.5px;font-weight:600;white-space:nowrap}
  .doc{padding:34px 20px 74px}
  .doc-h1{font-size:32px;line-height:1.15}
  .doc-h2{font-size:20px;margin:28px 0 12px}
  .doc-h3{font-size:16px}
  .doc p{font-size:15px}

  /* Feature theater: stack vertically */
  .ft{grid-template-columns:1fr;gap:14px}
  .ft-list{flex-direction:column}
  .ft-it{padding:8px 10px;font-size:14px}
  .ft-stage{min-height:200px;padding:16px}

  /* Table to cards */
  .doc-tbl{display:block;border:none}
  .doc-tbl thead{display:none}
  .doc-tbl tr{display:flex;flex-direction:column;margin-bottom:14px;border:1px solid var(--hair);
    border-radius:10px;overflow:hidden;box-shadow:var(--soft);background:#fff}
  .doc-tbl td{display:flex;flex-direction:column;border:none;border-top:1px solid var(--hair);padding:12px;text-align:left}
  .doc-tbl td:first-child{border-top:0}
  .doc-tbl tr:hover td{background:none}
  .tbl-flag td{background:none}
  .tbl-job{min-width:auto;margin-top:6px}

  /* Checklist adjustments */
  .doc-tasks{grid-template-columns:1fr;gap:0}
  .doc-tasks li{padding:10px 0}

  /* Chips */
  .doc-chips{gap:6px}
  .chip{font-size:12px;padding:5px 10px}

  /* FAQ */
  .doc-faq summary{font-size:15px;padding-right:24px}
  .doc-faq p{font-size:14px}

  /* Newsletter */
  .doc-news{flex-direction:column}
  .doc-news input{min-width:100%}
  .doc-news button{width:100%;white-space:nowrap}

  /* Footnotes */
  .doc-foot{columns:1;padding-left:0}
  .ed-status .st-live{display:none}
  .doc-foot{font-size:12px}

  /* Scenes */
  .sc-live{flex-direction:column;gap:8px}
  .sc-frame{min-width:140px;font-size:10px}
  .sc-deck{width:min(240px,85%)}
}

/* Extra small phones */
@media(max-width:500px){
  .ed-flip{flex-wrap:wrap}
  .ed-flip button{padding:6px 9px;font-size:11px}
  .ed-get{padding:6px 9px;font-size:11px}
  .doc-h1{font-size:26px}
  .doc-h2{font-size:18px;margin-top:24px}
  .doc{padding:28px 16px 74px}
  .ft-it{font-size:13px;padding:7px 8px}
  .ft-stage{min-height:180px;padding:12px}
  .doc-tasks li{padding:8px 0;font-size:13.5px}
  .doc p{font-size:14px;line-height:1.6}
  .doc-tbl tr{margin-bottom:12px}
  .doc-tbl td{padding:10px}
  .tbl-app{gap:8px}
  .chip{font-size:11.5px;padding:4px 9px}
}
`;

export default CosmovexLanding;
